import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Chip,
  Divider,
  Card,
  CardContent,
  Checkbox,
  Stack,
} from '@mui/material';
import ResponsiveDialog from './ResponsiveDialog';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Link as LinkIcon,
  Image as ImageIcon,
  DragIndicator as DragIndicatorIcon,
  Palette as PaletteIcon,
} from '@mui/icons-material';
import {
  cloneBaseCategoryColorOptions,
  hasBaseCategoryColorOptions,
  mergeBaseCategoryColorOptions,
} from '../utils/defaultCategoryColorOptions';
import {
  Category,
  CategoryAttribute,
  CategoryColissimoShipping,
  CategoryColissimoShippingForm,
  CreateCategoryRequest,
} from '../types/category';
import IconSelector from './IconSelector';
import CategoryIcon from './CategoryIcon';
import apiService from '../services/api';
import { getApiOrigin } from '../config/environment';
import { useQuery, useQueryClient } from '@tanstack/react-query';

const DEFAULT_COLISSIMO_FORM: CategoryColissimoShippingForm = {
  enabled: false,
  label: 'Colissimo standard',
  weight_kg: 1,
  product_code_home: 'DOM',
  product_code_pickup: '',
  internal_cost_estimate_cents: '',
};

function parseOptionsFromMultilineText(text: string): string[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

function conditionalOptionsToTextMap(options: Record<string, string[]>): Record<string, string> {
  return Object.fromEntries(
    Object.entries(options).map(([key, values]) => [key, values.join('\n')])
  );
}

function flushConditionalOptionsText(
  options: Record<string, string[]>,
  textByKey: Record<string, string>
): Record<string, string[]> {
  const flushed = { ...options };
  Object.keys(textByKey).forEach((key) => {
    if (key in flushed) {
      flushed[key] = parseOptionsFromMultilineText(textByKey[key] ?? '');
    }
  });
  return flushed;
}

function colissimoFormFromCategory(
  raw?: Category['colissimo_shipping'] | null
): CategoryColissimoShippingForm {
  if (!raw) {
    return { ...DEFAULT_COLISSIMO_FORM };
  }
  return {
    enabled: true,
    label: raw.label ?? DEFAULT_COLISSIMO_FORM.label,
    weight_kg: raw.weight_kg ?? 1,
    product_code_home: raw.product_code_home ?? 'DOM',
    product_code_pickup: raw.product_code_pickup ?? '',
    internal_cost_estimate_cents:
      raw.internal_cost_estimate_cents != null ? String(raw.internal_cost_estimate_cents) : '',
  };
}

interface CategoryDialogProps {
  open: boolean;
  onClose: () => void;
  category?: Category | null;
  onSave: (data: any) => void;
  isLoading: boolean;
}

const CategoryDialog: React.FC<CategoryDialogProps> = ({
  open,
  onClose,
  category,
  onSave,
  isLoading,
}) => {
  const queryClient = useQueryClient();
  const { data: providerTypesData, isLoading: loadingProviderTypes } = useQuery({
    queryKey: ['provider-types-entity'],
    queryFn: () => apiService.getProviderTypesFromEntity(),
    enabled: open,
    staleTime: 60_000,
  });
  const providerTypes = providerTypesData?.provider_types ?? [];
  const [localDefaultImageUrl, setLocalDefaultImageUrl] = useState<string | null>(null);
  const [defaultImageError, setDefaultImageError] = useState<string | null>(null);
  const [defaultImageLoading, setDefaultImageLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    icon: '',
    custom_icon_filename: '' as string | null,
    sort_order: 0,
    is_active: true,
    secret_display: false,
    disabled_provider_type_ids: [] as number[],
    secret_clues_placeholder: '' as string,
    storage_box_prefix: '' as string,
    packaging_instructions: '' as string,
    finder_message_templates: [] as string[],
    attributes: {} as Record<string, CategoryAttribute>,
    colissimo_shipping: { ...DEFAULT_COLISSIMO_FORM },
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [conditionalDialogOpen, setConditionalDialogOpen] = useState(false);
  const [selectedAttribute, setSelectedAttribute] = useState<string | null>(null);
  const [conditionalOptions, setConditionalOptions] = useState<Record<string, string[]>>({});
  const [addOptionDialogOpen, setAddOptionDialogOpen] = useState(false);
  const [selectedParentOption, setSelectedParentOption] = useState<string>('');
  const [colorDialogOpen, setColorDialogOpen] = useState(false);
  const [colorOptions, setColorOptions] = useState<Array<{ label: string; hex: string | null }>>([]);
  const [iconSelectorOpen, setIconSelectorOpen] = useState(false);
  const [attributeOrder, setAttributeOrder] = useState<string[]>([]);
  const [draggedAttributeKey, setDraggedAttributeKey] = useState<string | null>(null);
  const [selectOptionsText, setSelectOptionsText] = useState<Record<string, string>>({});
  const [conditionalOptionsText, setConditionalOptionsText] = useState<Record<string, string>>({});

  const rebuildAttributesInOrder = (
    attributes: Record<string, CategoryAttribute>,
    order: string[]
  ): Record<string, CategoryAttribute> => {
    const next: Record<string, CategoryAttribute> = {};
    order.forEach((key, index) => {
      if (attributes[key]) {
        next[key] = { ...attributes[key], sort_order: index };
      }
    });
    return next;
  };

  const sortCategoryAttributeKeys = (attributes: Record<string, CategoryAttribute>): string[] => {
    const keys = Object.keys(attributes);
    const hasSortOrder = keys.some((key) => typeof attributes[key]?.sort_order === 'number');
    if (!hasSortOrder) {
      return keys;
    }
    return keys.sort((a, b) => {
      const orderA = attributes[a]?.sort_order ?? Number.MAX_SAFE_INTEGER;
      const orderB = attributes[b]?.sort_order ?? Number.MAX_SAFE_INTEGER;
      if (orderA !== orderB) return orderA - orderB;
      return a.localeCompare(b);
    });
  };

  const reorderAttributeKeys = (keys: string[], fromIndex: number, toIndex: number): string[] => {
    if (fromIndex < 0 || toIndex < 0 || fromIndex >= keys.length || toIndex >= keys.length) {
      return keys;
    }
    const next = [...keys];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    return next;
  };

  const moveAttribute = (fromIndex: number, toIndex: number) => {
    setAttributeOrder((prev) => {
      const nextOrder = reorderAttributeKeys(prev, fromIndex, toIndex);
      setFormData((fd) => ({
        ...fd,
        attributes: rebuildAttributesInOrder(fd.attributes, nextOrder),
      }));
      return nextOrder;
    });
  };

  useEffect(() => {
    if (category) {
      const rawAttrs = category.attributes;
      const attrs =
        rawAttrs && typeof rawAttrs === 'object' && !Array.isArray(rawAttrs)
          ? (rawAttrs as Record<string, CategoryAttribute>)
          : {};
      setFormData({
        name: category.name,
        slug: category.slug,
        description: category.description || '',
        icon: category.icon || '',
        custom_icon_filename: category.custom_icon_filename ?? null,
        sort_order: category.sort_order,
        is_active: category.is_active,
        secret_display: category.secret_display ?? false,
        disabled_provider_type_ids: category.disabled_provider_type_ids ?? [],
        secret_clues_placeholder: category.secret_clues_placeholder || '',
        storage_box_prefix: category.storage_box_prefix || '',
        packaging_instructions: category.packaging_instructions || '',
        finder_message_templates: [...(category.finder_message_templates ?? [])],
        attributes: attrs,
        colissimo_shipping: colissimoFormFromCategory(category.colissimo_shipping),
      });
      setAttributeOrder(sortCategoryAttributeKeys(attrs));
      setLocalDefaultImageUrl(category.default_image_url ?? null);
      const nextSelectText: Record<string, string> = {};
      Object.entries(attrs).forEach(([key, attr]) => {
        if (attr.type === 'select' && Array.isArray(attr.options)) {
          nextSelectText[key] = attr.options.join('\n');
        }
      });
      setSelectOptionsText(nextSelectText);
    } else {
      setFormData({
        name: '',
        slug: '',
        description: '',
        icon: '',
        custom_icon_filename: null,
        sort_order: 0,
        is_active: true,
        secret_display: false,
        disabled_provider_type_ids: [],
        secret_clues_placeholder: '',
        storage_box_prefix: '',
        packaging_instructions: '',
        finder_message_templates: [],
        attributes: {},
        colissimo_shipping: { ...DEFAULT_COLISSIMO_FORM },
      });
      setAttributeOrder([]);
      setLocalDefaultImageUrl(null);
      setSelectOptionsText({});
    }
    setConditionalOptionsText({});
    setErrors({});
    setDefaultImageError(null);
    setDraggedAttributeKey(null);
  }, [category, open]);

  const apiBase = getApiOrigin();

  const handleDefaultImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !category?.id) return;
    setDefaultImageError(null);
    setDefaultImageLoading(true);
    try {
      const res = await apiService.uploadCategoryDefaultImage(category.id, file);
      setLocalDefaultImageUrl(res.data?.category?.default_image_url ?? null);
      queryClient.invalidateQueries({ queryKey: ['categories'] });
    } catch (err: unknown) {
      console.error(err);
      const ax = err as { response?: { data?: { error?: string; message?: string } } };
      setDefaultImageError(
        ax.response?.data?.error || ax.response?.data?.message || 'Échec du téléversement de l’image par défaut'
      );
    } finally {
      setDefaultImageLoading(false);
    }
    e.target.value = '';
  };

  const handleDeleteDefaultImage = async () => {
    if (!category?.id) return;
    setDefaultImageError(null);
    setDefaultImageLoading(true);
    try {
      await apiService.deleteCategoryDefaultImage(category.id);
      setLocalDefaultImageUrl(null);
      queryClient.invalidateQueries({ queryKey: ['categories'] });
    } catch (err: unknown) {
      console.error(err);
      const ax = err as { response?: { data?: { error?: string; message?: string } } };
      setDefaultImageError(
        ax.response?.data?.error || ax.response?.data?.message || 'Échec de la suppression'
      );
    } finally {
      setDefaultImageLoading(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleColissimoChange = (
    field: keyof CategoryColissimoShippingForm,
    value: string | number | boolean
  ) => {
    setFormData((prev) => ({
      ...prev,
      colissimo_shipping: {
        ...prev.colissimo_shipping,
        [field]: value,
      },
    }));
  };

  const handleAttributeChange = (attributeKey: string, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      attributes: {
        ...prev.attributes,
        [attributeKey]: {
          ...prev.attributes[attributeKey],
          [field]: value,
        },
      },
    }));
  };

  const handleAttributeTypeChange = (attributeKey: string, newType: string) => {
    setFormData((prev) => {
      const current = prev.attributes[attributeKey];
      if (!current) {
        return prev;
      }
      const next: CategoryAttribute = { ...current, type: newType as CategoryAttribute['type'] };
      if (newType === 'color_select' && !hasBaseCategoryColorOptions(next.options)) {
        next.options = cloneBaseCategoryColorOptions();
      }
      return {
        ...prev,
        attributes: {
          ...prev.attributes,
          [attributeKey]: next,
        },
      };
    });
  };

  const addAttribute = () => {
    const newKey = `attribute_${Date.now()}`;
    setFormData(prev => ({
      ...prev,
      attributes: {
        ...prev.attributes,
        [newKey]: {
          type: 'string',
          label: '',
          required: false,
          use_as_filter: false,
          use_in_title: false,
          secret_detail: false,
          disabled: false,
          sort_order: Object.keys(prev.attributes).length,
        },
      },
    }));
    setAttributeOrder((prev) => [...prev, newKey]);
  };

  const removeAttribute = (attributeKey: string) => {
    setFormData(prev => {
      const newAttributes = { ...prev.attributes };
      delete newAttributes[attributeKey];
      return { ...prev, attributes: newAttributes };
    });
    setAttributeOrder((prev) => prev.filter((k) => k !== attributeKey));
    setSelectOptionsText((prev) => {
      const next = { ...prev };
      delete next[attributeKey];
      return next;
    });
  };

  const orderedAttributeKeys = (() => {
    const keys = attributeOrder.filter((k) => k in formData.attributes);
    Object.keys(formData.attributes).forEach((k) => {
      if (!keys.includes(k)) keys.push(k);
    });
    return keys;
  })();

  const openConditionalDialog = (attributeKey: string) => {
    setSelectedAttribute(attributeKey);
    const attribute = formData.attributes[attributeKey];
    if (attribute && attribute.type === 'conditional_select' && attribute.options) {
      const options = attribute.options as Record<string, string[]>;
      setConditionalOptions(options);
      setConditionalOptionsText(conditionalOptionsToTextMap(options));
    } else {
      setConditionalOptions({});
      setConditionalOptionsText({});
    }
    setConditionalDialogOpen(true);
  };

  const closeConditionalDialog = () => {
    setConditionalDialogOpen(false);
    setSelectedAttribute(null);
    setConditionalOptions({});
    setConditionalOptionsText({});
  };

  const saveConditionalOptions = () => {
    if (selectedAttribute) {
      const flushed = flushConditionalOptionsText(conditionalOptions, conditionalOptionsText);
      handleAttributeChange(selectedAttribute, 'options', flushed);
    }
    closeConditionalDialog();
  };

  const addConditionalKey = () => {
    // Trouver l'attribut parent (celui qui a des options simples)
    const parentAttribute = Object.entries(formData.attributes).find(([_, attr]) => 
      attr.type === 'select' && Array.isArray(attr.options)
    );
    
    if (parentAttribute) {
      const [, parentAttr] = parentAttribute;
      const parentOptions = parentAttr.options as string[];
      
      // Filtrer les options déjà utilisées
      const availableOptions = parentOptions.filter(option => 
        !Object.keys(conditionalOptions).includes(option)
      );
      
      if (availableOptions.length > 0) {
        setSelectedParentOption(availableOptions[0]);
        setAddOptionDialogOpen(true);
      } else {
        // Toutes les options sont déjà utilisées
        alert('Toutes les options de l\'attribut parent sont déjà utilisées.');
      }
    } else {
      // Fallback si pas d'attribut parent trouvé
      const newKey = `Option_${Date.now()}`;
      setConditionalOptions(prev => ({
        ...prev,
        [newKey]: []
      }));
      setConditionalOptionsText(prev => ({
        ...prev,
        [newKey]: '',
      }));
    }
  };

  const confirmAddConditionalKey = () => {
    if (selectedParentOption) {
      setConditionalOptions(prev => ({
        ...prev,
        [selectedParentOption]: []
      }));
      setConditionalOptionsText(prev => ({
        ...prev,
        [selectedParentOption]: '',
      }));
    }
    setAddOptionDialogOpen(false);
    setSelectedParentOption('');
  };

  const removeConditionalKey = (key: string) => {
    setConditionalOptions(prev => {
      const newOptions = { ...prev };
      delete newOptions[key];
      return newOptions;
    });
    setConditionalOptionsText(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const updateConditionalKey = (oldKey: string, newKey: string) => {
    if (oldKey === newKey) return;
    setConditionalOptions(prev => {
      const newOptions = { ...prev };
      newOptions[newKey] = newOptions[oldKey];
      delete newOptions[oldKey];
      return newOptions;
    });
    setConditionalOptionsText(prev => {
      const next = { ...prev };
      next[newKey] = next[oldKey] ?? '';
      delete next[oldKey];
      return next;
    });
  };

  const updateConditionalOptionsText = (key: string, text: string) => {
    setConditionalOptionsText(prev => ({ ...prev, [key]: text }));
  };

  const commitConditionalOptionsText = (key: string, text: string) => {
    const parsed = parseOptionsFromMultilineText(text);
    setConditionalOptions(prev => ({ ...prev, [key]: parsed }));
    setConditionalOptionsText(prev => ({ ...prev, [key]: parsed.join('\n') }));
  };

  const openColorDialog = (attributeKey: string) => {
    setSelectedAttribute(attributeKey);
    const attribute = formData.attributes[attributeKey];
    if (
      attribute &&
      attribute.type === 'color_select' &&
      hasBaseCategoryColorOptions(attribute.options)
    ) {
      setColorOptions(attribute.options as Array<{ label: string; hex: string | null }>);
    } else {
      setColorOptions(cloneBaseCategoryColorOptions());
    }
    setColorDialogOpen(true);
  };

  const closeColorDialog = () => {
    setColorDialogOpen(false);
    setSelectedAttribute(null);
    setColorOptions([]);
  };

  const saveColorOptions = () => {
    if (selectedAttribute) {
      handleAttributeChange(selectedAttribute, 'options', colorOptions);
    }
    closeColorDialog();
  };

  const addColorOption = () => {
    setColorOptions(prev => [...prev, { label: '', hex: null }]);
  };

  const updateColorOption = (index: number, field: 'label' | 'hex', value: string) => {
    setColorOptions(prev => prev.map((option, i) => 
      i === index ? { ...option, [field]: value } : option
    ));
  };

  const removeColorOption = (index: number) => {
    setColorOptions(prev => prev.filter((_, i) => i !== index));
  };

  const applyBaseColorOptions = (mode: 'replace' | 'merge') => {
    if (mode === 'replace') {
      if (
        colorOptions.length > 0 &&
        !window.confirm(
          'Remplacer toutes les couleurs actuelles par la palette de base ? Les saisies en cours seront perdues.'
        )
      ) {
        return;
      }
      setColorOptions(cloneBaseCategoryColorOptions());
      return;
    }
    setColorOptions((prev) => mergeBaseCategoryColorOptions(prev));
  };

  const handleIconSelect = (iconName: string) => {
    if (!iconName) {
      handleInputChange('icon', '');
      handleInputChange('custom_icon_filename', null);
      return;
    }

    // Fichier SVG uploadé (liste /icons/list : nom de fichier)
    if (iconName.toLowerCase().endsWith('.svg')) {
      handleInputChange('icon', 'custom_svg');
      handleInputChange('custom_icon_filename', iconName);
      return;
    }

    // Icône Material-UI (nom court : smartphone, pets, …)
    handleInputChange('icon', iconName);
    handleInputChange('custom_icon_filename', null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Le nom est requis';
    }

    if (!formData.slug.trim()) {
      newErrors.slug = 'Le slug est requis';
    } else if (!/^[a-z0-9-_]+$/.test(formData.slug)) {
      newErrors.slug = 'Le slug ne peut contenir que des lettres minuscules, chiffres, tirets et underscores';
    }

    // Validation des attributs
    Object.entries(formData.attributes).forEach(([key, attr]) => {
      if (!attr.label.trim()) {
        newErrors[`attr_${key}_label`] = 'Le libellé est requis';
      }
    });

    const prefixRaw = (formData.storage_box_prefix || '').trim();
    if (prefixRaw.length > 0) {
      const letters = prefixRaw.replace(/[^A-Za-z]/g, '');
      if (letters.length < 3) {
        newErrors.storage_box_prefix =
          'Le préfixe doit contenir au moins 3 lettres (chiffres et symboles ignorés), ex. TEL ou smartphone → SMA.';
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const { colissimo_shipping: _colissimoForm, ...formRest } = formData;

    const attributesWithFlushedOptions = { ...formData.attributes };
    Object.entries(attributesWithFlushedOptions).forEach(([key, attr]) => {
      if (attr.type === 'select') {
        const rawText = selectOptionsText[key];
        if (rawText !== undefined) {
          attributesWithFlushedOptions[key] = {
            ...attr,
            options: parseOptionsFromMultilineText(rawText),
          };
        }
      }
    });

    const payload: CreateCategoryRequest = {
      ...formRest,
      attributes: rebuildAttributesInOrder(attributesWithFlushedOptions, orderedAttributeKeys),
    };

    const secretPlaceholder = (payload.secret_clues_placeholder || '').trim();
    payload.secret_clues_placeholder = secretPlaceholder !== '' ? secretPlaceholder : null;

    const packagingText = (payload.packaging_instructions || '').trim();
    payload.packaging_instructions = packagingText !== '' ? packagingText : null;

    const normalizedTemplates = formData.finder_message_templates
      .map((line) => line.trim())
      .filter((line) => line !== '');
    payload.finder_message_templates = normalizedTemplates.length > 0 ? normalizedTemplates : null;

    if (!(payload.storage_box_prefix || '').trim()) {
      payload.storage_box_prefix = '';
    }
    if (!payload.custom_icon_filename || !String(payload.custom_icon_filename).trim()) {
      payload.custom_icon_filename = null;
    }

    if (formData.colissimo_shipping.enabled) {
      const cs = formData.colissimo_shipping;
      const internalRaw = cs.internal_cost_estimate_cents.trim();
      const colissimoPayload: CategoryColissimoShipping & { enabled: boolean } = {
        enabled: true,
        label: cs.label.trim() || DEFAULT_COLISSIMO_FORM.label,
        weight_kg: Math.max(0.01, Math.min(30, Number(cs.weight_kg) || 1)),
        product_code_home: (cs.product_code_home || 'DOM').trim().toUpperCase(),
        product_code_pickup: cs.product_code_pickup.trim()
          ? cs.product_code_pickup.trim().toUpperCase()
          : null,
        internal_cost_estimate_cents:
          internalRaw === '' ? null : Math.max(0, Number.parseInt(internalRaw, 10) || 0),
      };
      payload.colissimo_shipping = colissimoPayload;
    } else {
      payload.colissimo_shipping = null;
    }

    onSave(payload);
  };

  const attributeTypes = [
    { value: 'string', label: 'Texte court' },
    { value: 'text', label: 'Texte long' },
    { value: 'select', label: 'Sélection' },
    { value: 'conditional_select', label: 'Sélection conditionnelle' },
    { value: 'color_select', label: 'Sélection de couleur' },
    { value: 'boolean', label: 'Oui/Non' },
    { value: 'date', label: 'Date' },
    { value: 'number', label: 'Nombre' },
  ];

  return (
    <ResponsiveDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {category ? 'Modifier la catégorie' : 'Nouvelle catégorie'}
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Grid container spacing={3}>
            {/* Informations de base */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Informations de base
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nom de la catégorie"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                error={!!errors.name}
                helperText={errors.name}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Slug"
                value={formData.slug}
                onChange={(e) => handleInputChange('slug', e.target.value)}
                error={!!errors.slug}
                helperText={errors.slug || 'Identifiant unique (lettres minuscules, chiffres, tirets)'}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                multiline
                rows={3}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box>
                <TextField
                  fullWidth
                  label="Icône"
                  value={formData.icon}
                  onChange={(e) => handleInputChange('icon', e.target.value)}
                  placeholder="Ex: phone, wallet, keys..."
                  InputProps={{
                    endAdornment: (
                      <Button
                        type="button"
                        variant="outlined"
                        size="small"
                        startIcon={<ImageIcon />}
                        onClick={() => setIconSelectorOpen(true)}
                        sx={{ ml: 1 }}
                      >
                        Choisir
                      </Button>
                    ),
                  }}
                />
                {(formData.icon || formData.custom_icon_filename) && (
                  <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    <Typography variant="caption" color="text.secondary">
                      Icône actuelle :
                    </Typography>
                    <Box
                      title={formData.custom_icon_filename || formData.icon}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 1,
                        borderRadius: 1,
                        border: 1,
                        borderColor: 'divider',
                        bgcolor: 'action.hover',
                        minWidth: 48,
                        minHeight: 48,
                      }}
                    >
                      <CategoryIcon
                        icon={formData.icon}
                        customIconFilename={formData.custom_icon_filename}
                        size="medium"
                      />
                    </Box>
                    <IconButton
                      type="button"
                      size="small"
                      aria-label="Retirer l'icône"
                      onClick={() => {
                        handleInputChange('icon', '');
                        handleInputChange('custom_icon_filename', null);
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Ordre d'affichage"
                type="number"
                value={formData.sort_order}
                onChange={(e) => handleInputChange('sort_order', parseInt(e.target.value) || 0)}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => handleInputChange('is_active', e.target.checked)}
                  />
                }
                label="Catégorie active"
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Masquer pour les types de partenaires
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                La catégorie reste active globalement mais n’apparaît pas lors de la création d’annonce ou d’objet
                trouvé pour ces types de partenaires.
              </Typography>
              {loadingProviderTypes ? (
                <CircularProgress size={20} />
              ) : providerTypes.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Aucun type de partenaire configuré.
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {providerTypes.map((pt) => {
                    const checked = formData.disabled_provider_type_ids.includes(pt.id);
                    return (
                      <FormControlLabel
                        key={pt.id}
                        control={
                          <Checkbox
                            checked={checked}
                            onChange={(e) => {
                              const next = e.target.checked
                                ? [...formData.disabled_provider_type_ids, pt.id]
                                : formData.disabled_provider_type_ids.filter((id) => id !== pt.id);
                              handleInputChange('disabled_provider_type_ids', next);
                            }}
                          />
                        }
                        label={pt.name}
                      />
                    );
                  })}
                </Box>
              )}
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.secret_display}
                    onChange={(e) => handleInputChange('secret_display', e.target.checked)}
                  />
                }
                label="Affichage secret (masquer les photos réelles sur le site public — ex. pièces d’identité)"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Placeholder — détails secrets"
                value={formData.secret_clues_placeholder}
                onChange={(e) => handleInputChange('secret_clues_placeholder', e.target.value)}
                placeholder="Ex. Numéro de série, initiales gravées, couleur de la coque…"
                helperText={
                  'Texte d’aide dans le champ « Détails secrets » à la création d’annonce (étape infos secrètes). Laissez vide pour le texte par défaut.'
                }
                inputProps={{ maxLength: 255 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Préfixe boîtes de stockage (3 lettres)"
                value={formData.storage_box_prefix}
                onChange={(e) => handleInputChange('storage_box_prefix', e.target.value.toUpperCase())}
                placeholder="Ex. TEL, SAC, DOC"
                inputProps={{ maxLength: 8 }}
                error={!!errors.storage_box_prefix}
                helperText={
                  errors.storage_box_prefix ||
                  'Utilisé pour nommer les boîtes sur les lieux partenaires : TEL-1, TEL-2… Laissez vide si non utilisé.'
                }
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                maxRows={8}
                label="Consigne d'emballage"
                value={formData.packaging_instructions}
                onChange={(e) => handleInputChange('packaging_instructions', e.target.value)}
                placeholder="Ex. Utilisez une enveloppe à bulles. Protégez l'écran avec du carton rigide."
                helperText="Affichée au trouveur dans la messagerie et par e-mail une fois la livraison payée par le perdeur."
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 1 }}>
                Messages type (messagerie trouveur)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Raccourcis proposés au trouveur au-dessus du champ de saisie pour répondre rapidement au perdeur.
              </Typography>
              <Stack spacing={1.5}>
                {formData.finder_message_templates.map((template, index) => (
                  <Box key={`finder-template-${index}`} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      maxRows={4}
                      label={`Message ${index + 1}`}
                      value={template}
                      onChange={(e) => {
                        const next = [...formData.finder_message_templates];
                        next[index] = e.target.value;
                        setFormData((prev) => ({ ...prev, finder_message_templates: next }));
                      }}
                      placeholder="Ex. Bonjour, merci pour votre intérêt ! L'objet est toujours disponible."
                    />
                    <IconButton
                      aria-label="Supprimer ce message type"
                      onClick={() => {
                        const next = formData.finder_message_templates.filter((_, i) => i !== index);
                        setFormData((prev) => ({ ...prev, finder_message_templates: next }));
                      }}
                      sx={{ mt: 1 }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    if (formData.finder_message_templates.length >= 20) return;
                    setFormData((prev) => ({
                      ...prev,
                      finder_message_templates: [...prev.finder_message_templates, ''],
                    }));
                  }}
                  disabled={formData.finder_message_templates.length >= 20}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Ajouter un message type
                </Button>
              </Stack>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 1 }}>
                Envoi Colissimo
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Définit le poids et le code produit Colissimo pour les objets de cette catégorie. Le perdeur paie un
                forfait fixe ; le bon d&apos;expédition utilise ces paramètres (selon votre contrat La Poste).
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.colissimo_shipping.enabled}
                    onChange={(e) => handleColissimoChange('enabled', e.target.checked)}
                  />
                }
                label="Profil d'envoi personnalisé"
              />
            </Grid>

            {formData.colissimo_shipping.enabled && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Libellé (affiché au perdeur)"
                    value={formData.colissimo_shipping.label}
                    onChange={(e) => handleColissimoChange('label', e.target.value)}
                    placeholder="Ex. Lettre verte suivie"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Poids du colis (kg)"
                    value={formData.colissimo_shipping.weight_kg}
                    onChange={(e) =>
                      handleColissimoChange('weight_kg', parseFloat(e.target.value) || 0)
                    }
                    inputProps={{ min: 0.01, max: 30, step: 0.01 }}
                    helperText="Ex. 0,1 pour une lettre, 1 à 2 pour un petit colis"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Code produit — domicile"
                    value={formData.colissimo_shipping.product_code_home}
                    onChange={(e) =>
                      handleColissimoChange('product_code_home', e.target.value.toUpperCase())
                    }
                    placeholder="DOM"
                    helperText="Codes courants : DOM, DOS, COL, COM (selon contrat)"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Code produit — point relais"
                    value={formData.colissimo_shipping.product_code_pickup}
                    onChange={(e) =>
                      handleColissimoChange('product_code_pickup', e.target.value.toUpperCase())
                    }
                    placeholder="BPR ou A2P (vide = auto selon le relais)"
                    helperText="Laisser vide pour déduire du type de point relais"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Coût interne estimé (centimes)"
                    value={formData.colissimo_shipping.internal_cost_estimate_cents}
                    onChange={(e) =>
                      handleColissimoChange('internal_cost_estimate_cents', e.target.value)
                    }
                    helperText="Optionnel — suivi interne (écart avec le forfait perdeur)"
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Image par défaut
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Utilisée si l’annonce n’a pas de photo ; si « affichage secret » est activé, seule cette image est montrée aux visiteurs.
              </Typography>
              {defaultImageError && (
                <Alert severity="error" sx={{ mb: 1 }} onClose={() => setDefaultImageError(null)}>
                  {defaultImageError}
                </Alert>
              )}
              <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                {localDefaultImageUrl && (
                  <Box
                    component="img"
                    src={localDefaultImageUrl ? `${apiBase}${localDefaultImageUrl}` : ''}
                    alt="Aperçu"
                    sx={{ maxHeight: 96, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
                    onError={() => setDefaultImageError('Impossible d’afficher l’aperçu (URL ou CORS). Vérifiez REACT_APP_API_URL.')}
                  />
                )}
                {category?.id ? (
                  <>
                    <Button variant="outlined" component="label" size="small" disabled={defaultImageLoading}>
                      {defaultImageLoading ? (
                        <CircularProgress size={18} sx={{ mr: 1 }} />
                      ) : null}
                      {localDefaultImageUrl ? 'Remplacer l’image' : 'Téléverser une image'}
                      <input
                        type="file"
                        hidden
                        accept="image/jpeg,image/png,image/webp"
                        onChange={handleDefaultImageUpload}
                        disabled={defaultImageLoading}
                      />
                    </Button>
                    {localDefaultImageUrl && (
                      <Button
                        type="button"
                        size="small"
                        color="error"
                        variant="text"
                        onClick={handleDeleteDefaultImage}
                        disabled={defaultImageLoading}
                      >
                        Supprimer
                      </Button>
                    )}
                  </>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    Enregistrez la catégorie, puis rouvrez-la pour téléverser une image par défaut.
                  </Typography>
                )}
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
            </Grid>

            {/* Gestion des attributs */}
            <Grid item xs={12}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  Attributs ({Object.keys(formData.attributes).length})
                </Typography>
                <Button
                  type="button"
                  startIcon={<AddIcon />}
                  onClick={addAttribute}
                  variant="outlined"
                  size="small"
                >
                  Ajouter un attribut
                </Button>
              </Box>
              {orderedAttributeKeys.length > 1 && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  Glissez les attributs par la poignée ⋮⋮ pour modifier l&apos;ordre d&apos;affichage.
                </Typography>
              )}
            </Grid>

            {orderedAttributeKeys.map((key, index) => {
              const attribute = formData.attributes[key];
              if (!attribute) return null;
              return (
              <Grid
                item
                xs={12}
                key={key}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  if (!draggedAttributeKey || draggedAttributeKey === key) return;
                  const fromIndex = orderedAttributeKeys.indexOf(draggedAttributeKey);
                  const toIndex = index;
                  if (fromIndex >= 0 && toIndex >= 0) moveAttribute(fromIndex, toIndex);
                  setDraggedAttributeKey(null);
                }}
                sx={{
                  opacity: draggedAttributeKey === key ? 0.55 : 1,
                  transition: 'opacity 0.15s ease',
                }}
              >
                <Accordion
                  sx={{
                    opacity: attribute.disabled ? 0.72 : 1,
                    border:
                      draggedAttributeKey === key
                        ? '2px dashed'
                        : '1px solid rgba(0,0,0,0.08)',
                    borderColor: draggedAttributeKey === key ? 'primary.main' : 'rgba(0,0,0,0.08)',
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box display="flex" alignItems="center" width="100%">
                      <IconButton
                        type="button"
                        size="small"
                        aria-label="Déplacer l'attribut"
                        draggable
                        onDragStart={(e) => {
                          e.stopPropagation();
                          setDraggedAttributeKey(key);
                          e.dataTransfer.effectAllowed = 'move';
                          e.dataTransfer.setData('text/plain', key);
                        }}
                        onDragEnd={() => setDraggedAttributeKey(null)}
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          cursor: 'grab',
                          mr: 0.5,
                          color: 'text.secondary',
                          '&:active': { cursor: 'grabbing' },
                        }}
                      >
                        <DragIndicatorIcon fontSize="small" />
                      </IconButton>
                      <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                        {attribute.label || `Attribut ${key}`}
                      </Typography>
                      <Chip
                        label={attributeTypes.find(t => t.value === attribute.type)?.label || attribute.type}
                        size="small"
                        sx={{ mr: 1 }}
                      />
                      {attribute.use_as_filter && (
                        <Chip label="Filtre partenaire" size="small" color="secondary" variant="outlined" sx={{ mr: 1 }} />
                      )}
                      {attribute.use_in_title && (
                        <Chip label="Titre" size="small" color="info" variant="outlined" sx={{ mr: 1 }} />
                      )}
                      {attribute.disabled && (
                        <Chip label="Désactivé" size="small" color="warning" variant="outlined" sx={{ mr: 1 }} />
                      )}
                      <IconButton
                        type="button"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeAttribute(key);
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Clé de l'attribut"
                          value={key}
                          disabled
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Libellé"
                          value={attribute.label}
                          onChange={(e) => handleAttributeChange(key, 'label', e.target.value)}
                          error={!!errors[`attr_${key}_label`]}
                          helperText={errors[`attr_${key}_label`]}
                          size="small"
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Type</InputLabel>
                          <Select
                            value={attribute.type}
                            onChange={(e) => handleAttributeTypeChange(key, e.target.value)}
                            label="Type"
                          >
                            {attributeTypes.map((type) => (
                              <MenuItem key={type.value} value={type.value}>
                                {type.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={attribute.required}
                              onChange={(e) => handleAttributeChange(key, 'required', e.target.checked)}
                            />
                          }
                          label="Requis"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={!!attribute.disabled}
                              onChange={(e) => handleAttributeChange(key, 'disabled', e.target.checked)}
                            />
                          }
                          label="Désactivé"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: -1 }}>
                          Un attribut désactivé reste en base (annonces existantes) mais n&apos;apparaît plus à la création.
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={!!attribute.secret_detail}
                              onChange={(e) => handleAttributeChange(key, 'secret_detail', e.target.checked)}
                            />
                          }
                          label="Détail secret — masqué sur les annonces publiques ; visible pour le créateur et en fiche objet partenaire (détails secrets)"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={!!attribute.use_as_filter}
                              onChange={(e) => handleAttributeChange(key, 'use_as_filter', e.target.checked)}
                            />
                          }
                          label="Utiliser comme filtre — afficher cet attribut comme filtre sur la liste publique des objets trouvés (Sherlook Pro), après choix de cette catégorie"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={!!attribute.use_in_title}
                              onChange={(e) => handleAttributeChange(key, 'use_in_title', e.target.checked)}
                            />
                          }
                          label="Utiliser pour le titre — ajoute la valeur saisie au titre automatique de l'annonce (ordre des attributs respecté)"
                        />
                      </Grid>
                      {(attribute.type === 'string' || attribute.type === 'text' || attribute.type === 'number') && (
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Placeholder"
                            value={attribute.placeholder || ''}
                            onChange={(e) => handleAttributeChange(key, 'placeholder', e.target.value)}
                            size="small"
                          />
                        </Grid>
                      )}
                      {attribute.type === 'select' && (
                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Options (une par ligne)
                          </Typography>
                          <TextField
                            fullWidth
                            multiline
                            minRows={4}
                            maxRows={12}
                            value={
                              selectOptionsText[key] ??
                              (Array.isArray(attribute.options) ? attribute.options.join('\n') : '')
                            }
                            onChange={(e) => {
                              setSelectOptionsText((prev) => ({ ...prev, [key]: e.target.value }));
                            }}
                            onBlur={(e) => {
                              const parsed = parseOptionsFromMultilineText(e.target.value);
                              handleAttributeChange(key, 'options', parsed);
                              setSelectOptionsText((prev) => ({ ...prev, [key]: parsed.join('\n') }));
                            }}
                            size="small"
                            placeholder={'Option 1\nOption 2\nOption 3'}
                            helperText="Une option par ligne. Maj+Entrée ou Entrée pour passer à la ligne suivante."
                          />
                        </Grid>
                      )}
                      {attribute.type === 'conditional_select' && (
                        <Grid item xs={12}>
                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                            <Typography variant="body2" color="text.secondary">
                              Options conditionnelles
                            </Typography>
                            <Button
                              type="button"
                              size="small"
                              startIcon={<LinkIcon />}
                              onClick={() => openConditionalDialog(key)}
                              variant="outlined"
                            >
                              Configurer
                            </Button>
                          </Box>
                          {attribute.options && typeof attribute.options === 'object' && (
                            <Card variant="outlined">
                              <CardContent sx={{ p: 2 }}>
                                <Typography variant="caption" color="text.secondary" gutterBottom>
                                  Configuration actuelle :
                                </Typography>
                                {Object.entries(attribute.options as Record<string, string[]>).map(([optionKey, values]) => (
                                  <Box key={optionKey} sx={{ mb: 1 }}>
                                    <Typography variant="body2" fontWeight="bold">
                                      {optionKey}: {values.length} option(s)
                                    </Typography>
                                  </Box>
                                ))}
                              </CardContent>
                            </Card>
                          )}
                        </Grid>
                      )}
                      {attribute.type === 'color_select' && (
                        <Grid item xs={12}>
                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                            <Typography variant="body2" color="text.secondary">
                              Options de couleur
                            </Typography>
                            <Button
                              type="button"
                              size="small"
                              startIcon={<LinkIcon />}
                              onClick={() => openColorDialog(key)}
                              variant="outlined"
                            >
                              Configurer
                            </Button>
                          </Box>
                          {attribute.options && Array.isArray(attribute.options) && (
                            <Card variant="outlined">
                              <CardContent sx={{ p: 2 }}>
                                <Typography variant="caption" color="text.secondary" gutterBottom>
                                  Configuration actuelle :
                                </Typography>
                                <Box display="flex" flexWrap="wrap" gap={1}>
                                  {(attribute.options as Array<{ label: string; hex: string | null }>).map((option, index) => (
                                    <Box key={index} display="flex" alignItems="center" gap={0.5}>
                                      {option.hex && (
                                        <Box
                                          sx={{
                                            width: 16,
                                            height: 16,
                                            backgroundColor: option.hex,
                                            border: '1px solid #ccc',
                                            borderRadius: '50%',
                                          }}
                                        />
                                      )}
                                      <Typography variant="body2">
                                        {option.label}
                                      </Typography>
                                    </Box>
                                  ))}
                                </Box>
                              </CardContent>
                            </Card>
                          )}
                        </Grid>
                      )}
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Grid>
            );
            })}

            {Object.keys(formData.attributes).length === 0 && (
              <Grid item xs={12}>
                <Alert severity="info">
                  Aucun attribut défini. Cliquez sur "Ajouter un attribut" pour commencer.
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button type="button" onClick={onClose} disabled={isLoading}>
            Annuler
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
          >
            {isLoading ? <CircularProgress size={20} /> : (category ? 'Modifier' : 'Créer')}
          </Button>
        </DialogActions>
      </form>

      {/* Dialog pour la configuration des options conditionnelles */}
      <ResponsiveDialog open={conditionalDialogOpen} onClose={closeConditionalDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Configuration des options conditionnelles
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Configurez les options qui apparaîtront selon la valeur sélectionnée dans l'attribut parent.
          </Typography>
          
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Options conditionnelles ({Object.keys(conditionalOptions).length})
            </Typography>
            <Button
              startIcon={<AddIcon />}
              onClick={addConditionalKey}
              variant="outlined"
              size="small"
            >
              Ajouter une option
            </Button>
          </Box>

          {Object.entries(conditionalOptions).map(([key, values]) => {
            // Trouver l'attribut parent pour obtenir ses options
            const parentAttribute = Object.entries(formData.attributes).find(([_, attr]) => 
              attr.type === 'select' && Array.isArray(attr.options)
            );
            const parentOptions = parentAttribute ? (parentAttribute[1].options as string[]) : [];
            
            return (
              <Card key={key} variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    {parentOptions.length > 0 ? (
                      <FormControl size="small" sx={{ flexGrow: 1, mr: 2 }}>
                        <InputLabel>Valeur de l'option parent</InputLabel>
                        <Select
                          value={key}
                          onChange={(e) => updateConditionalKey(key, e.target.value)}
                          label="Valeur de l'option parent"
                        >
                          {parentOptions.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <TextField
                        label="Valeur de l'option parent"
                        value={key}
                        onChange={(e) => updateConditionalKey(key, e.target.value)}
                        size="small"
                        sx={{ flexGrow: 1, mr: 2 }}
                      />
                    )}
                    <IconButton
                      onClick={() => removeConditionalKey(key)}
                      color="error"
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>

                  <Typography variant="subtitle2" gutterBottom>
                    Options disponibles pour &quot;{key}&quot; ({values.length})
                  </Typography>

                  <TextField
                    fullWidth
                    multiline
                    minRows={4}
                    maxRows={12}
                    size="small"
                    label="Options (une par ligne)"
                    value={conditionalOptionsText[key] ?? values.join('\n')}
                    onChange={(e) => updateConditionalOptionsText(key, e.target.value)}
                    onBlur={(e) => commitConditionalOptionsText(key, e.target.value)}
                    placeholder={'Option 1\nOption 2\nOption 3'}
                    helperText="Une option par ligne."
                    sx={{ mt: 1 }}
                  />
                </CardContent>
              </Card>
            );
          })}

          {Object.keys(conditionalOptions).length === 0 && (
            <Alert severity="info">
              Aucune option conditionnelle définie. Cliquez sur "Ajouter une option" pour commencer.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConditionalDialog}>
            Annuler
          </Button>
          <Button onClick={saveConditionalOptions} variant="contained">
            Sauvegarder
          </Button>
        </DialogActions>
      </ResponsiveDialog>

      {/* Dialog pour ajouter une option conditionnelle */}
      <ResponsiveDialog open={addOptionDialogOpen} onClose={() => setAddOptionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Ajouter une option conditionnelle
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Sélectionnez la valeur de l'option parent pour laquelle vous voulez définir des options conditionnelles.
          </Typography>
          
          {(() => {
            // Trouver l'attribut parent pour obtenir ses options
            const parentAttribute = Object.entries(formData.attributes).find(([_, attr]) => 
              attr.type === 'select' && Array.isArray(attr.options)
            );
            const parentOptions = parentAttribute ? (parentAttribute[1].options as string[]) : [];
            const availableOptions = parentOptions.filter(option => 
              !Object.keys(conditionalOptions).includes(option)
            );
            
            return (
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Option parent</InputLabel>
                <Select
                  value={selectedParentOption}
                  onChange={(e) => setSelectedParentOption(e.target.value)}
                  label="Option parent"
                >
                  {availableOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOptionDialogOpen(false)}>
            Annuler
          </Button>
          <Button onClick={confirmAddConditionalKey} variant="contained" disabled={!selectedParentOption}>
            Ajouter
          </Button>
        </DialogActions>
      </ResponsiveDialog>

      {/* Dialog pour la configuration des couleurs */}
      <ResponsiveDialog open={colorDialogOpen} onClose={closeColorDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Configuration des couleurs
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Configurez les couleurs disponibles avec leur nom et leur code hexadécimal.
          </Typography>
          
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
            flexWrap="wrap"
            gap={1}
          >
            <Typography variant="h6">
              Couleurs ({colorOptions.length})
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={1}>
              <Button
                startIcon={<PaletteIcon />}
                onClick={() => applyBaseColorOptions(colorOptions.length === 0 ? 'replace' : 'merge')}
                variant="contained"
                size="small"
              >
                {colorOptions.length === 0 ? 'Générer les couleurs de base' : 'Compléter les couleurs de base'}
              </Button>
              {colorOptions.length > 0 && (
                <Button
                  startIcon={<PaletteIcon />}
                  onClick={() => applyBaseColorOptions('replace')}
                  variant="outlined"
                  size="small"
                >
                  Réinitialiser la palette
                </Button>
              )}
              <Button
                startIcon={<AddIcon />}
                onClick={addColorOption}
                variant="outlined"
                size="small"
              >
                Ajouter une couleur
              </Button>
            </Box>
          </Box>

          {colorOptions.map((option, index) => (
            <Card key={index} variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Nom de la couleur"
                      value={option.label}
                      onChange={(e) => updateColorOption(index, 'label', e.target.value)}
                      size="small"
                      placeholder="Ex: Rouge, Bleu, Vert..."
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Code hexadécimal"
                      value={option.hex || ''}
                      onChange={(e) => updateColorOption(index, 'hex', e.target.value)}
                      size="small"
                      placeholder="#FF0000"
                      inputProps={{ maxLength: 7 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Box display="flex" alignItems="center" gap={1}>
                      {option.hex && (
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            backgroundColor: option.hex,
                            border: '2px solid #ccc',
                            borderRadius: 1,
                          }}
                        />
                      )}
                      <Typography variant="caption" color="text.secondary">
                        Aperçu
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={1}>
                    <IconButton
                      onClick={() => removeColorOption(index)}
                      color="error"
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}

          {colorOptions.length === 0 && (
            <Alert severity="info">
              Aucune couleur définie. Utilisez « Générer les couleurs de base » pour préremplir une palette
              (noir, blanc, rouge, bleu, etc.) ou ajoutez des couleurs une par une.
            </Alert>
          )}

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Conseil :</strong> Utilisez des codes hexadécimaux valides (ex: #FF0000 pour rouge, #00FF00 pour vert, #0000FF pour bleu).
              Vous pouvez laisser le code hex vide si vous ne voulez pas afficher de pastille de couleur.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeColorDialog}>
            Annuler
          </Button>
          <Button onClick={saveColorOptions} variant="contained">
            Sauvegarder
          </Button>
        </DialogActions>
      </ResponsiveDialog>

      {/* Sélecteur d'icônes */}
      <IconSelector
        open={iconSelectorOpen}
        onClose={() => setIconSelectorOpen(false)}
        onSelect={handleIconSelect}
        currentIcon={formData.icon}
        currentCustomFilename={formData.custom_icon_filename}
      />
    </ResponsiveDialog>
  );
};

export default CategoryDialog;
